import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';

interface SessionEventPayload {
  id?: string;
  isActive?: boolean;
  is_active?: boolean;
}

/**
 * Subscribe to the current session updates and force logout on invalidation.
 */
export function useSessionWebSocket() {
  const { sessionId, logout } = useAuth();
  const { state, addObserver, removeObserver } = useWebSocket();
  const navigate = useNavigate();
  const hasLoggedOutRef = useRef(false);

  const handleEvent = useCallback(
    (data: SessionEventPayload) => {
      if (hasLoggedOutRef.current) {
        return;
      }
      const isActive = data?.isActive ?? data?.is_active;
      if (isActive === false) {
        hasLoggedOutRef.current = true;
        void logout();
        navigate('/login?reason=session');
      }
    },
    [logout, navigate],
  );

  useEffect(() => {
    if (!sessionId || !state.isConnected) {
      return;
    }

    const observerId = addObserver(
      'user_session',
      { data: { target: sessionId } },
      (data: SessionEventPayload) => handleEvent(data),
      {
        includeCreateEvents: true,
        includeUpdateEvents: true,
        includeDeleteEvents: true,
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
      },
    );

    return () => {
      removeObserver(observerId);
    };
  }, [sessionId, state.isConnected, addObserver, removeObserver, handleEvent]);
}
